import {
  isBridgeEnvelope,
  NATIVE_BRIDGE_VERSION,
  type BiltBridgeEnvelope,
  type BiltNativeEvent,
  type BiltNativeRequest,
} from './nativeBridgeWeb';

export type NativeCapabilities = {
  native_bridge_version: number;
  platform: 'ios' | 'android';
  app_version: string;
  qr_scan: boolean;
  gate_scan: boolean;
  ticket_scan: boolean;
  native_camera: boolean;
  native_share: boolean;
  haptics: boolean;
  push: boolean;
  offline_events: boolean;
  system_settings: boolean;
  native_files: boolean;
  nfc: boolean;
  tap_to_pay: boolean;
};

export type ScanMode = 'auto' | 'passport' | 'gate' | 'ticket' | 'campaign' | 'url';

export type PhotoPurpose =
  | 'vehicle_gallery'
  | 'maintenance_receipt'
  | 'avatar'
  | 'entity_logo'
  | 'passport_memory'
  | 'generic';

export type PhotoBridgePayload = {
  ok: boolean;
  uri?: string;
  uploadId?: string | number;
  focalX?: number;
  focalY?: number;
  error?: string;
};

declare global {
  interface Window {
    __BILT_NATIVE_SHELL__?: {
      version: number;
      platform: 'ios' | 'android';
      capabilities: NativeCapabilities;
    };
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export function isRunningInBiltNativeShell(): boolean {
  return Boolean(window.__BILT_NATIVE_SHELL__?.version);
}

export function getNativeCapabilities(): NativeCapabilities | null {
  return window.__BILT_NATIVE_SHELL__?.capabilities ?? null;
}

export function nativeShellPlatform(): 'ios_app' | 'android_app' | null {
  const platform = window.__BILT_NATIVE_SHELL__?.platform;
  if (platform === 'ios') return 'ios_app';
  if (platform === 'android') return 'android_app';
  return null;
}

/** True when embedded in the native capability shell. */
export function hasNativeShell(): boolean {
  return isRunningInBiltNativeShell();
}

/** Check a native capability flag from GET_NATIVE_CAPABILITIES. */
export function hasNativeCapability(
  key: keyof Omit<NativeCapabilities, 'native_bridge_version' | 'platform' | 'app_version'>
): boolean {
  const caps = getNativeCapabilities();
  if (!caps) return false;
  return Boolean(caps[key]);
}

/** Gate web features that require a newer native bridge binary. */
export function requiresBridgeVersion(minVersion: number): boolean {
  const version = window.__BILT_NATIVE_SHELL__?.version ?? 0;
  return version >= minVersion;
}

function newBridgeRequestId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function postToNative(payload: BiltNativeRequest, id?: string) {
  if (!window.ReactNativeWebView?.postMessage) return;
  const envelope: BiltBridgeEnvelope = {
    source: 'bilt-web',
    v: NATIVE_BRIDGE_VERSION,
    id,
    payload,
  };
  window.ReactNativeWebView.postMessage(JSON.stringify(envelope));
}

export function requestNativeCapabilities(): void {
  postToNative({ type: 'GET_NATIVE_CAPABILITIES' });
}

export function requestNativeLogout(): void {
  postToNative({ type: 'NATIVE_LOGOUT' });
}

type NativeSessionTokens = {
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
  user: {
    id: number;
    username: string;
    email: string;
    [key: string]: unknown;
  };
};

/** Push web session tokens to native Secure Store (shell WebView only). */
export function syncNativeSession(tokens: NativeSessionTokens): void {
  if (!tokens.accessToken || !tokens.user?.id) return;
  postToNative({
    type: 'SYNC_NATIVE_SESSION',
    session: {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: tokens.expiresAt,
      user: tokens.user,
    },
  });
}

export type OpenNativeScannerOptions = {
  mode?: ScanMode;
  /** Required for gate / ticket modes — opens `/organizer/:eventId/scan`. */
  eventId?: number;
};

/**
 * Launch a native QR camera overlay.
 * Passport FAB uses mode passport/auto (default).
 * Organizer gate must pass mode gate|ticket and eventId.
 */
export function openNativeScanner(
  modeOrOptions?: ScanMode | OpenNativeScannerOptions,
  eventIdMaybe?: number
): void {
  let mode: ScanMode | undefined;
  let eventId: number | undefined;
  if (typeof modeOrOptions === 'string' || modeOrOptions === undefined) {
    mode = modeOrOptions;
    eventId = eventIdMaybe;
  } else {
    mode = modeOrOptions.mode;
    eventId = modeOrOptions.eventId;
  }
  const payload: BiltNativeRequest = { type: 'OPEN_SCANNER' };
  if (mode) payload.mode = mode;
  if (typeof eventId === 'number' && Number.isInteger(eventId) && eventId > 0) {
    payload.eventId = eventId;
  }
  postToNative(payload);
}

export function nativeShare(url: string, title?: string): void {
  postToNative({ type: 'SHARE', url, title });
}

export function nativeHaptic(style: 'light' | 'medium' | 'success' | 'warning' | 'error' = 'light'): void {
  postToNative({ type: 'HAPTIC', style });
}

export function setScanButtonHidden(hidden: boolean): void {
  postToNative({ type: hidden ? 'SCAN_BUTTON_HIDE' : 'SCAN_BUTTON_SHOW' });
}

export function setScanButtonOffset(bottomPx: number): void {
  postToNative({ type: 'SCAN_BUTTON_OFFSET', bottomPx });
}

export function captureNativePhoto(options: {
  purpose: PhotoPurpose;
  vehicleId?: number;
  modificationId?: number;
  maintenanceId?: number;
  encounterPublicId?: string;
  source?: 'camera' | 'library';
}): void {
  postToNative({
    type: 'CAPTURE_PHOTO',
    purpose: options.purpose,
    vehicleId: options.vehicleId,
    modificationId: options.modificationId,
    maintenanceId: options.maintenanceId,
    encounterPublicId: options.encounterPublicId,
    source: options.source ?? 'camera',
  });
}

/**
 * Request native camera/library capture and wait for PHOTO_RESULT (or NATIVE_ERROR).
 * Native may upload for some purposes (vehicle_gallery + vehicleId, avatar, …).
 */
export function requestNativePhoto(options: {
  purpose: PhotoPurpose;
  vehicleId?: number;
  modificationId?: number;
  maintenanceId?: number;
  encounterPublicId?: string;
  source?: 'camera' | 'library';
  timeoutMs?: number;
}): Promise<PhotoBridgePayload> {
  if (!isRunningInBiltNativeShell() || !hasNativeCapability('native_camera')) {
    return Promise.resolve({ ok: false, error: 'native_camera_unavailable' });
  }

  const id = newBridgeRequestId('photo');
  const timeoutMs = options.timeoutMs ?? 120_000;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (payload: PhotoBridgePayload) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(payload);
    };

    const timer = setTimeout(() => finish({ ok: false, error: 'timeout' }), timeoutMs);

    const cleanup = installNativeBridgeHost((event, envelopeId) => {
      if (envelopeId && envelopeId !== id) return;
      if (event.type === 'PHOTO_RESULT') {
        const photo = (event as { payload?: PhotoBridgePayload }).payload;
        finish(photo && typeof photo === 'object' ? photo : { ok: false, error: 'empty_result' });
        return;
      }
      if (event.type === 'NATIVE_ERROR') {
        finish({
          ok: false,
          error: String((event as { code?: string; message?: string }).code || 'native_error'),
        });
      }
    });

    postToNative(
      {
        type: 'CAPTURE_PHOTO',
        purpose: options.purpose,
        vehicleId: options.vehicleId,
        modificationId: options.modificationId,
        maintenanceId: options.maintenanceId,
        encounterPublicId: options.encounterPublicId,
        source: options.source ?? 'camera',
      },
      id
    );
  });
}

export function requestNativePushStatus(): void {
  postToNative({ type: 'GET_PUSH_STATUS' });
}

export function requestNativePushPermission(): void {
  postToNative({ type: 'REQUEST_PUSH_PERMISSION' });
}

export function openNativeSystemSettings(): void {
  postToNative({ type: 'OPEN_SYSTEM_SETTINGS' });
}

export function setNativeAppBadge(count: number): void {
  postToNative({ type: 'SET_APP_BADGE', count });
}

export function requestNativeConnectivity(): void {
  postToNative({ type: 'GET_CONNECTIVITY' });
}

export function installNativeBridgeHost(
  onEvent?: (event: BiltNativeEvent, envelopeId?: string) => void
): () => void {
  const handler = (event: MessageEvent) => {
    if (typeof event.data !== 'string') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!isBridgeEnvelope(parsed) || parsed.source !== 'bilt-native') return;
    onEvent?.(parsed.payload as BiltNativeEvent, parsed.id);
  };
  window.addEventListener('message', handler);
  document.addEventListener('message', handler as EventListener);
  return () => {
    window.removeEventListener('message', handler);
    document.removeEventListener('message', handler as EventListener);
  };
}
